import React from 'react';
import PropTypes from 'prop-types';

import { mainHeader, subHeader, subjectArea } from './Info.less';

import { mapMSNQTotalToCognitiveSymptoms, mapMSNQScoresToCognitiveChallenges } from 'neuro-calculation-commons';

const cognitiveSymptomsLocalizationsEN = {
  mild: 'Based on your self-rating, you perceive at most occasional / mild cognitive symptoms',
  moderate: 'Based on your self-rating, you perceive at least occasional / mild cognitive symptoms',
  severe: 'Based on your self-rating, you perceive cognitive symptoms affecting daily life',
  verySevere: 'Based on your self-rating, you perceive marked cognitive symptoms'
};

const cognitiveChallengesLocalizationsEN = {
  concentration: 'concentration',
  memoryLearning: 'memory and new learning',
  problemSolving: 'problem solving, verbal functions, cognitive flexibility, and behavioral regulation'
};

const InfoEN = ({ totalScore, statusMood, statusFatigue, document }) => (
  <React.Fragment>
    <div className={mainHeader}>
      Feedback on the self-perceived cognitive problems based on the Multiple Sclerosis Neuropsychological Questionnaire
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Total score</div>
      <ul>
        <li>
          Your score was <span style={{ fontWeight: 600 }}>{totalScore}</span> points (where min 0, max 60; higher score
          refers to more severe self-perceived cognitive problems).
        </li>
      </ul>
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Self-perceived cognitive symptoms</div>
      <ul>
        <li>{cognitiveSymptomsLocalizationsEN[mapMSNQTotalToCognitiveSymptoms(totalScore)]}</li>
      </ul>
    </div>

    {mapMSNQScoresToCognitiveChallenges(document.content).length > 0 && (
      <div>
        <ul>
          <li>Based on your self-rating, you may have at least occasionally problems with:</li>
          <ul>
            {mapMSNQScoresToCognitiveChallenges(document.content).map(challenge => (
              <li>{cognitiveChallengesLocalizationsEN[challenge]}</li>
            ))}
          </ul>
        </ul>
      </div>
    )}

    {(statusFatigue > 0 || statusMood > 0) && (
      <div className={subjectArea}>
        <span>
          It is important to notice that your self-perceived cognitive symptoms may be at least partly explained by
          {statusFatigue > 0 && ' self-perceived fatigue'} {statusFatigue > 0 && statusMood > 0 && ' and'}{' '}
          {statusMood > 0 && ' self-perceived problems in mood'}. It is important to discuss these problems with a
          specialized nurse or your treating neurologist.
        </span>
      </div>
    )}

    <div className={subjectArea}>
      <div className={subHeader}>Information on cognitive symptoms</div>
      The most typical cognitive challenges are problems with new learning and information processing speed,
      susceptibility to interference, and cognitive fatigability. You may need more time to take care of your duties.
      You may also have to be prepared to make an alternative plan when things are not going as you have planned. People
      with MS may notice challenges with word finding. Especially when fatigued even familiar words may be missing. More
      seldom people with MS have problems in visual perception, for example finding their way in familiar surroundings.
      People with MS tell that fatigue may reduce mental flexibility and resilience. That is why it is important to try
      to perform the most important and challenging duties when you are most alert.
      <br />
      <br />
      It is easier to come along with your cognitive challenges when you are aware of them and can modify your daily
      routines. Aids and compensatory strategies are often useful. You might have advantage of the following:
    </div>

    <div className={subjectArea}>
      <div className={subHeader}>Useful tips</div>
      Concentration
      <br />
      <ul>
        <li>if your attention is susceptible to interference</li>
        <ul>
          <li>prefer peaceful surroundings</li>
          <li>try to avoid disruptions</li>
          <li>use head phones, turn off radio, TV etc</li>
          <li>avoid hustle and rush</li>
        </ul>
        <li>if multitasking is difficult</li>
        <ul>
          <li>avoid sharing your attention to several duties</li>
          <li>focus your attention to a single task at a time</li>
          <li>write down a list of duties</li>
          <li>take enough time to perform the duties</li>
        </ul>
        <li>
          if it is difficult for you to sustain your attention for a longer period of time or if you fatigue easily
        </li>
        <ul>
          <li>make brief pauses several times per day</li>
          <li>perform the most important and challenging duties when you are most alert</li>
          <li>
            try to avoid working too long without a break – suggest breaks during duties which require continuous
            cognitive effort
          </li>
        </ul>
      </ul>
      <br />
      New learning and memory
      <ul>
        <li>if learning is more difficult than it used to be</li>
        <ul>
          <li>try to pay attention to the most important things</li>
          <li>try to be systematic</li>
          <li>find a peaceful space for learning</li>
          <li>try to take enough time</li>
          <li>make breaks</li>
          <li>divide the material into smaller, more manageable units</li>
          <li>try to apply the new material into practice</li>
          <li>familiarize yourself into the way you learn most effectively – by using pictures, mind maps, etc.</li>
        </ul>
        <li>if you forget more easily</li>
        <ul>
          <li>use different memory aids – calendars, note books, memos, recording talks, etc.</li>
          <li>ask your family members to support you in the use of memory aids</li>
          <li>apply the aids at work, at home, when studying, in hobbies, etc.</li>
        </ul>
      </ul>
      <br />
      Cognitive flexibility, word finding, behavioural regulation
      <ul>
        <li>if new situations are difficult for you</li>
        <ul>
          <li>take extra time</li>
          <li>think before you perform or before you take the first steps – calm down</li>
          <li>make an action plan with predetermined steps</li>
        </ul>
        <li>if word finding is difficult</li>
        <ul>
          <li>try to speak more slowly</li>
          <li>
            tell your family members whether you wish them to help you or whether you prefer extra time in finding the
            missing word
          </li>
        </ul>
        <li>if you feel that you are not acting in the way you wish or used to act</li>
        <ul>
          <li>tell your family members about your concerns</li>
          <li>
            try to find the reasons for the change – fatigue, lowered mood, or uncertainty for example may explain
            changes in your behavior
          </li>
          <li>try to avoid situations with excessive emotional load</li>
        </ul>
        <li>distractors and fatigue may affect your behavior</li>
        <ul>
          <li>try to remove distractors</li>
          <li>pay attention to your alertness, make breaks systematically</li>
          <li>
            ask your family members for feedback (especially if they recognize that you are fatigued but you are not
            aware of it)
          </li>
        </ul>
      </ul>
    </div>
    <div className={subjectArea}>
      <div className={subHeader}>And next...</div>
      It is important that you stay active both physically and cognitively. Everyday activities and use of cognitive
      abilities in different ways promote cognitive preservation. If cognitive problems affect your daily life, or
      ability to work or study, discuss with your MS-nurse or neurologist. It might be important to consider whether a
      neuropsychological assessment should be performed. Feedback from the assessment as well as neuropsychological
      counselling or rehabilitation may be helpful. You can find more information from{' '}
      <a href="https://www.neuroliitto.fi" target="blank">
        www.neuroliitto.fi
      </a>{' '}
      (information on the Finnish Neuro Society),{' '}
      <a href="https://www.kela.fi" target="blank">
        www.kela.fi
      </a>{' '}
      (information on rehabilitation options), and{' '}
      <a href="https://www.appeco.net" target="blank">
        www.appeco.net
      </a>{' '}
      (information on the effects of rehabilitation).
    </div>
  </React.Fragment>
);

InfoEN.propTypes = {
  totalScore: PropTypes.number,
  statusMood: PropTypes.number,
  statusFatigue: PropTypes.number,
  document: PropTypes.object
};

export default InfoEN;
